import React from 'react';
import { motion } from 'framer-motion';
import { PRODUCTS } from '../data/scenarios';
import { Check } from 'lucide-react';

const ProductCard = ({ product, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 + index * 0.1 }}
    className="product-card"
    style={{ background: product.color, borderColor: `${product.accentColor}30` }}
  >
    {/* Tag */}
    <div className="flex items-center justify-between mb-3">
      <span
        className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full"
        style={{ background: product.accentColor, color: 'hsl(0 0% 100%)' }}
      >
        {product.tag}
      </span>
      <span className="text-2xl">{product.icon}</span>
    </div>

    {/* Name */}
    <h3 className="text-sm font-semibold mb-0.5" style={{ color: 'hsl(var(--foreground))' }}>
      {product.name}
    </h3>
    <p className="text-xs mb-3" style={{ color: 'hsl(var(--muted-foreground))' }}>{product.subtitle}</p>

    {/* Description */}
    <p className="text-xs leading-relaxed flex-1 mb-4" style={{ color: 'hsl(var(--foreground))' }}>
      {product.description}
    </p>

    {/* Features */}
    <div className="flex flex-col gap-1.5 mb-4">
      {product.features.map(f => (
        <div key={f} className="flex items-center gap-1.5">
          <Check size={11} color={product.accentColor} className="flex-shrink-0" />
          <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>{f}</span>
        </div>
      ))}
    </div>

    {/* Usage */}
    <div
      className="mt-auto rounded-lg px-3 py-2 text-xs"
      style={{ background: 'hsl(0 0% 100% / 0.7)', color: 'hsl(var(--muted-foreground))' }}
    >
      <span className="font-medium" style={{ color: product.accentColor }}>Дозировка: </span>
      {product.usage}
    </div>
  </motion.div>
);

const ProductShowcase = ({ onBack }) => {
  return (
    <div className="min-h-screen" style={{ background: 'hsl(var(--background))' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 px-5 py-4"
        style={{
          background: 'hsl(var(--card))',
          borderBottom: '1.5px solid hsl(var(--border))',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Линейка Синомарин</h2>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>5 продуктов для любой ситуации</p>
          </div>
          <button
            onClick={onBack}
            className="text-xs font-medium px-3 py-1.5 rounded-lg"
            style={{
              background: 'hsl(var(--muted))',
              color: 'hsl(var(--muted-foreground))',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--border))'}
            onMouseLeave={e => e.currentTarget.style.background = 'hsl(var(--muted))'}
          >
            ← Назад
          </button>
        </div>
      </div>

      {/* Product grid */}
      <div className="px-5 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Feature highlight */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-6 mb-6 flex items-center gap-5"
            style={{
              background: 'hsl(var(--card))',
              border: '1.5px solid hsl(var(--border))',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--gradient-ocean)' }}
            >
              <span style={{ fontSize: 28 }}>🌊</span>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1" style={{ color: 'hsl(var(--foreground))' }}>
                100% натуральная гипертоническая морская вода
              </h3>
              <p className="text-xs leading-relaxed" style={{ color: 'hsl(var(--muted-foreground))' }}>
                2.3% NaCl · 80+ минералов · Без консервантов · Без привыкания · Сертифицировано CE · 40+ стран
              </p>
            </div>
          </motion.div>

          {/* Cards grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {PRODUCTS.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>

          {/* Comparison table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-8 rounded-2xl overflow-hidden"
            style={{ border: '1.5px solid hsl(var(--border))', boxShadow: 'var(--shadow-sm)' }}
          >
            <div
              className="px-5 py-3"
              style={{ background: 'var(--gradient-ocean)' }}
            >
              <p className="text-sm font-semibold" style={{ color: 'hsl(0 0% 100%)' }}>Быстрый гид: кому что рекомендовать</p>
            </div>
            <div style={{ background: 'hsl(var(--card))' }}>
              {[
                { who: 'Новорождённые и дети до 5 лет', product: 'Синомарин Беби', note: 'Изотонический, мягкий' },
                { who: 'Дети 3–12 лет', product: 'Синомарин Кидс', note: 'Гипертонический' },
                { who: 'Взрослые (простуда)', product: 'Синомарин Взрослый', note: 'Базовая гипертоника' },
                { who: 'Сухость, хронический ринит', product: 'Синомарин Плюс', note: 'Algomer™ комплекс' },
                { who: 'Аллергический ринит', product: 'Синомарин Аллергия', note: 'Промывание аллергенов' },
              ].map((row, i) => (
                <div
                  key={row.who}
                  className="px-5 py-3.5 flex items-center gap-3"
                  style={{
                    borderTop: i > 0 ? '1px solid hsl(var(--border))' : 'none',
                  }}
                >
                  <div className="flex-1">
                    <p className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>{row.who}</p>
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>{row.note}</p>
                  </div>
                  <span
                    className="text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0"
                    style={{
                      background: 'hsl(201 82% 94%)',
                      color: 'hsl(201 82% 35%)',
                    }}
                  >
                    {row.product}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductShowcase;
